<template>
    <div>

        <Head title="Change Password" />
        <div class="flex justify-start mb-8 max-w-3xl">
            <h1 class="text-3xl font-bold">
                <Link class="text-orange-400 hover:text-orange-600" href="/users">Users</Link>
                <span class="text-orange-400 font-medium">/</span> Change Password
            </h1>
        </div>

        <div class="w-1/3 bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="changePassword">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <template v-if="isSelf">
                        <div class="relative pb-8 pr-6 w-full lg:w-1/1">
                            <text-input v-model="form.old_password" :error="form.errors.old_password"
                                :type="oldVisible ? 'text' : 'password'" label="Current Password" />
                            <button type="button" aria-label="Toggle current password visibility"
                                class="absolute flex hover:text-gray-700 inset-y-0 items-center pr-6 pt-2 right-3 text-gray-500"
                                @click="oldVisible = !oldVisible">
                                <i :class="['fa', oldVisible ? 'fa-eye-slash' : 'fa-eye']"></i>
                            </button>
                        </div>
                    </template>

                    <div class="relative pb-8 pr-6 w-full lg:w-1/1">
                        <text-input v-model="form.password" :error="form.errors.password"
                            :type="newVisible ? 'text' : 'password'" label="New Password" />
                        <button type="button" aria-label="Toggle new password visibility"
                            class="absolute flex hover:text-gray-700 inset-y-0 items-center pr-6 pt-2 right-3 text-gray-500"
                            @click="newVisible = !newVisible">
                            <i :class="['fa', newVisible ? 'fa-eye-slash' : 'fa-eye']"></i>
                        </button>
                    </div>
                    <div class="relative pb-8 pr-6 w-full lg:w-1/1">
                        <text-input v-model="form.password_confirmation" :error="form.errors.password_confirmation"
                            :type="confirmVisible ? 'text' : 'password'" label="Confirm Password" />
                        <button type="button" aria-label="Toggle confirm password visibility"
                            class="absolute flex hover:text-gray-700 inset-y-0 items-center pr-6 pt-2 right-3 text-gray-500"
                            @click="confirmVisible = !confirmVisible">
                            <i :class="['fa', confirmVisible ? 'fa-eye-slash' : 'fa-eye']"></i>
                        </button>
                    </div>

                    <div class="w-full px-6 pb-4">
                        <ul class="text-sm text-gray-600">
                            <li :class="{ 'text-orange-600': passwordChecks.length }">Minimum 8 characters</li>
                            <li :class="{ 'text-orange-600': passwordChecks.upper }">Contains upper-case letter</li>
                            <li :class="{ 'text-orange-600': passwordChecks.lower }">Contains lower-case letter</li>
                            <li :class="{ 'text-orange-600': passwordChecks.number }">Contains a number</li>
                            <li :class="{ 'text-orange-600': passwordChecks.symbol }">Contains a symbol</li>
                        </ul>
                    </div>
                </div>

                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit"
                        :disabled="form.processing || (isSelf && !form.old_password) || !passwordValid">Change
                        Password</loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import { Head, Link, useForm, usePage } from '@inertiajs/inertia-vue3'
    import { ref, toRefs, inject, computed } from 'vue'
    import Layout from '@/Shared/Layout'
    import TextInput from '@/Shared/TextInput'
    import LoadingButton from '@/Shared/LoadingButton'

    export default {
        components: {
            Head,
            Link,
            TextInput,
            LoadingButton,
        },
        layout: Layout,
        props: {
            user: Object,
        },
        setup(props) {
            const { user } = toRefs(props)
            const swal = inject('$swal')

            const form = useForm({
                old_password: '',
                password: '',
                password_confirmation: ''
            })

            // Determine if the current visitor is the user being edited
            const page = usePage()
            const currentUser = page.props.value.auth?.user ?? null
            const isSelf = currentUser ? (currentUser.id === user.value.id) : false

            const oldVisible = ref(false)
            const newVisible = ref(false)
            const confirmVisible = ref(false)

            const passwordChecks = computed(() => ({
                length: form.password.length >= 8,
                upper: /[A-Z]/.test(form.password),
                lower: /[a-z]/.test(form.password),
                number: /[0-9]/.test(form.password),
                symbol: /[^A-Za-z0-9]/.test(form.password),
            }))
            const passwordValid = computed(() => Object.values(passwordChecks.value).every(v => v))

            const changePassword = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You want to change the password for this user",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Prevent submission when password doesn't meet strength or old password required and missing
                        if (!passwordValid.value) {
                            swal.fire('Error', 'Password does not meet strength requirements.', 'error')
                            return
                        }
                        if (isSelf && !form.old_password) {
                            swal.fire('Error', 'Current password is required.', 'error')
                            return
                        }
                        form.put(`/users/${user.value.id}/password`, {
                            onSuccess: () => {
                                form.reset('old_password', 'password', 'password_confirmation')
                                swal.fire('Updated!', 'Password has been changed.', 'success')
                            }
                        })
                    }
                })
            }

            return {
                user,
                form,
                isSelf,
                oldVisible,
                newVisible,
                confirmVisible,
                passwordChecks,
                passwordValid,
                changePassword,
                swal,
            }
        }
    }
</script>
